import { useMemo } from 'react'

import { useGetMyCPDPoints, useGetMyRegistrations } from '@/utils/api/membership-service'
import type { CPDEventParticipantResponseDTO } from '@/utils/api/membership-service/models/cpdevent-participant-response-dt-o'
import type { MyCPDPointsResponseDTO } from '@/utils/api/membership-service/models/my-cpd-points-response-dt-o'
import type { RestPageCPDParticipant } from '@/utils/api/membership-service/models/rest-page-cpd-participant'

export interface CpdEventSummary {
  id: string
  title: string
  pointsEarned: number
  eventPoints: number
  theme?: string
  recipientName?: string
  memberName?: string
  participantEmail?: string
  participantPhone?: string
  completedOn: string | null
  certificateAvailable: boolean
}

export interface CpdSummary {
  year: number
  totalPoints: number
  requiredPoints: number
  eventsCompleted: number
  lastUpdated: string
  recentEvents: CpdEventSummary[]
}

const MAX_RECENT_EVENTS = 100
const DEFAULT_REQUIRED_POINTS = 40

type ParticipantWithExtras = CPDEventParticipantResponseDTO & {
  points?: number | null
  pointsEarned?: number | null
  awardedPoints?: number | null
  completedOn?: string | null
  updatedAt?: string | null
  certificateAvailable?: boolean
  event?: ParticipantWithExtras
  cpdEvent?: ParticipantWithExtras
}

type PointsSummaryPayload = MyCPDPointsResponseDTO & {
  recentEvents?: ParticipantWithExtras[]
  year?: number | string
  cpdYear?: number | string
  currentYear?: number | string
  lastUpdated?: string | null
  updatedAt?: string | null
  timestamp?: string | null
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return fallback
}

function toIsoDate(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) {
      return new Date(parsed).toISOString()
    }
  }
  return null
}

function resolveEventSource(participant: ParticipantWithExtras): ParticipantWithExtras {
  return participant.event ?? participant.cpdEvent ?? participant
}

function resolvePoints(participant: ParticipantWithExtras): number {
  const eventSource = resolveEventSource(participant)
  return toNumber(
    participant.points ??
      participant.pointsEarned ??
      participant.awardedPoints ??
      eventSource.points,
    0,
  )
}

function resolveEventPoints(participant: ParticipantWithExtras): number {
  const eventSource = resolveEventSource(participant)
  return toNumber(
    participant.eventPoints ??
      eventSource.eventPoints ??
      eventSource.points ??
      participant.points,
    0,
  )
}

function resolveCompletedOn(participant: ParticipantWithExtras): string | null {
  const eventSource = resolveEventSource(participant)
  return (
    toIsoDate(participant.completedOn) ??
    toIsoDate(participant.certificateIssuedAt) ??
    toIsoDate(participant.attendanceMarkedAt) ??
    toIsoDate(participant.registeredAt) ??
    toIsoDate((eventSource as { completedOn?: string }).completedOn) ??
    toIsoDate((eventSource as { certificateIssuedAt?: string }).certificateIssuedAt) ??
    toIsoDate((eventSource as { attendanceMarkedAt?: string }).attendanceMarkedAt) ??
    toIsoDate((eventSource as { endAt?: string }).endAt) ??
    toIsoDate((eventSource as { endDate?: string }).endDate) ??
    toIsoDate((eventSource as { startAt?: string }).startAt) ??
    null
  )
}

function mapParticipantToSummary(participant: ParticipantWithExtras, index: number): CpdEventSummary {
  const eventSource = resolveEventSource(participant)
  const rawId = participant.id ?? participant.eventId ?? eventSource.id ?? `event-${index}`

  const title =
    eventSource.eventTitle ??
    (eventSource as { title?: string }).title ??
    (eventSource as { name?: string }).name ??
    `CPD Event ${index + 1}`

  const statusText = (participant.status ?? eventSource.status)?.toString().toLowerCase()
  const certificateAvailable =
    Boolean(
      participant.certificateIssued ??
        participant.certificateAvailable ??
        (eventSource as { certificateIssued?: boolean }).certificateIssued ??
        (eventSource as { certificateAvailable?: boolean }).certificateAvailable,
    ) || statusText === 'completed'

  const rawTheme =
    participant.theme ??
    eventSource.theme ??
    (eventSource as { eventTheme?: string }).eventTheme
  const theme =
    typeof rawTheme === 'string' && rawTheme.trim().length > 0
      ? rawTheme.trim()
      : undefined

  return {
    id: String(rawId),
    title,
    pointsEarned: participant.pointsEarned ?? resolvePoints(participant),
    eventPoints: resolveEventPoints(participant),
    theme,
    recipientName: participant.memberName ?? (eventSource as { participantName?: string }).participantName,
    memberName: participant.memberName ?? (eventSource as { participantName?: string }).participantName,
    participantEmail: participant.memberEmail ?? (eventSource as { memberEmail?: string }).memberEmail,
    participantPhone: participant.memberPhone ?? (eventSource as { memberPhone?: string }).memberPhone,
    completedOn: resolveCompletedOn(participant),
    certificateAvailable,
  }
}

function buildRecentEvents(
  pointsPayload: PointsSummaryPayload | undefined,
  registrationsPage: RestPageCPDParticipant | undefined,
): CpdEventSummary[] {
  const summaryEvents = Array.isArray(pointsPayload?.recentEvents)
    ? pointsPayload!.recentEvents!.map((participant, index) => mapParticipantToSummary(participant, index))
    : []

  const registrationEvents = Array.isArray(registrationsPage?.content)
    ? registrationsPage!.content!.map((participant, index) =>
        mapParticipantToSummary(participant as ParticipantWithExtras, summaryEvents.length + index),
      )
    : []

  const merged = [...summaryEvents, ...registrationEvents]
  const unique = new Map<string, CpdEventSummary>()

  merged.forEach((event, index) => {
    const key = event.id || `event-${index}`
    if (!unique.has(key)) {
      unique.set(key, event)
    }
  })

  return Array.from(unique.values()).slice(0, MAX_RECENT_EVENTS)
}

function resolveYear(pointsPayload: PointsSummaryPayload | undefined): number {
  const fallback = new Date().getFullYear()
  const candidate = pointsPayload?.year ?? pointsPayload?.cpdYear ?? pointsPayload?.currentYear
  const numeric = toNumber(candidate, fallback)
  return Number.isFinite(numeric) ? Math.trunc(numeric) : fallback
}

function resolveLastUpdated(pointsPayload: PointsSummaryPayload | undefined): string {
  return (
    toIsoDate(pointsPayload?.updatedAt) ??
    toIsoDate(pointsPayload?.lastUpdated) ??
    toIsoDate(pointsPayload?.timestamp) ??
    new Date().toISOString()
  )
}

export interface CpdSummaryQueryResult {
  data: CpdSummary | undefined
  isPending: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  refetch: () => Promise<void>
}

export function useCpdSummaryQuery(): CpdSummaryQueryResult {
  const cpdPointsQuery = useGetMyCPDPoints({
    query: {
      select: (response) => response.data as PointsSummaryPayload | undefined,
    },
  })

  const registrationsQuery = useGetMyRegistrations(
    { page: 0, size: MAX_RECENT_EVENTS },
    {
      query: {
        select: (response) => response.data as RestPageCPDParticipant | undefined,
      },
    },
  )

  const isPending = cpdPointsQuery.isPending || registrationsQuery.isPending
  const isFetching = cpdPointsQuery.isFetching || registrationsQuery.isFetching
  const isError = Boolean(cpdPointsQuery.isError || registrationsQuery.isError)
  const error =
    (cpdPointsQuery.error ?? registrationsQuery.error ?? null) as Error | null

  const data = useMemo<CpdSummary | undefined>(() => {
    if (!cpdPointsQuery.data || !registrationsQuery.data) return undefined

    const pointsPayload = cpdPointsQuery.data
    const registrationsPayload = registrationsQuery.data

    const recentEvents = buildRecentEvents(pointsPayload, registrationsPayload)
    const eventsCompleted =
      pointsPayload?.eventsCompleted ??
      (recentEvents.length > 0 ? recentEvents.length : 0)

    const totalPointsFromPayload = toNumber(pointsPayload?.totalPoints, 0)
    const totalPointsFromEvents = recentEvents.reduce((sum, event) => sum + (event.pointsEarned ?? 0), 0)
    const totalPoints =
      totalPointsFromPayload > 0 ? totalPointsFromPayload : totalPointsFromEvents

    return {
      year: resolveYear(pointsPayload),
      totalPoints,
      requiredPoints: Math.max(
        toNumber(pointsPayload?.requiredPoints, DEFAULT_REQUIRED_POINTS),
        0,
      ),
      eventsCompleted,
      lastUpdated: resolveLastUpdated(pointsPayload),
      recentEvents,
    }
  }, [cpdPointsQuery.data, registrationsQuery.data])

  const refetch = async () => {
    await Promise.all([cpdPointsQuery.refetch(), registrationsQuery.refetch()])
  }

  return {
    data,
    isPending,
    isFetching,
    isError,
    error,
    refetch,
  }
}
